import { useEffect, useRef, useState } from "react";
import { displayModel } from "@/lib/brand";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { safeApiError } from "@/lib/safeErrorText";
import {
  Disc3, Sparkles, Flame, Clock, Loader2, Music2, Headphones, Share2, Play, Pause,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function timeAgo(iso) {
  try {
    const t = new Date(iso).getTime();
    const diff = Math.max(0, (Date.now() - t) / 1000);
    if (diff < 60) return `${Math.round(diff)}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
    return `${Math.round(diff / (86400 * 30))}mo ago`;
  } catch (e) { void e; return null; }
}

function fmtDuration(sec) {
  if (!Number.isFinite(sec) || sec <= 0) return null;
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function AmbientBackdrop() {
  return (
    <div className="pointer-events-none fixed inset-0 z-0 opacity-60">
      <div className="absolute -left-40 top-0 h-[460px] w-[460px] rounded-full bg-fuchsia-600/[0.22] blur-3xl" />
      <div className="absolute right-[-220px] top-40 h-[540px] w-[540px] rounded-full bg-sky-500/[0.18] blur-3xl" />
      <div className="absolute bottom-0 left-1/3 h-[400px] w-[400px] rounded-full bg-emerald-500/[0.10] blur-3xl" />
    </div>
  );
}

/**
 * A single session card — links to /session/{id}. The thumbnail is a stylized
 * gradient block driven by the session's tempo (hue) and plays count (sat).
 * This keeps the grid colorful without needing actual cover art uploaded by users.
 */
function SessionCard({ session, idx }) {
  // Deterministic hue per session so the same card always renders the same color.
  // 360° hue rotation across [60 BPM .. 200 BPM] sweep.
  const hue = Math.round(((session.tempo - 60) / 140) * 320) % 360;
  const dur = fmtDuration(session.duration);
  const playsLabel = Number(session.plays || 0).toLocaleString();

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(idx, 12) * 0.03 }}
      data-testid={`explore-session-${session.id}`}
    >
      <Link
        to={`/session/${session.id}`}
        className="group block rounded-2xl border border-white/[0.08] bg-white/[0.025] p-3 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.05] hover:shadow-[0_22px_46px_-24px_rgba(232,121,249,0.45)]"
      >
        {/* Cover — stylized gradient + concentric arcs that loosely resemble a
            vinyl record. Purely visual; replaceable with real cover art later. */}
        <div
          className="relative mb-3 aspect-square overflow-hidden rounded-xl"
          style={{
            background: `radial-gradient(circle at 30% 25%, hsla(${hue}, 90%, 70%, 0.95), transparent 55%),
                         linear-gradient(160deg, hsl(${hue}, 65%, 32%) 0%, hsl(${(hue + 70) % 360}, 70%, 14%) 100%)`,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 12px rgba(0,0,0,0.55)",
          }}
        >
          <div className="absolute inset-3 rounded-full border border-white/15" />
          <div className="absolute inset-6 rounded-full border border-white/10" />
          <div className="absolute inset-10 rounded-full border border-white/[0.07]" />
          <Disc3 className="absolute inset-0 m-auto h-10 w-10 text-white/85 drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:rotate-180" />
          <div className="absolute bottom-2 right-2 rounded-full bg-black/45 px-2 py-0.5 font-mono text-[9px] tabular-nums text-white/85 backdrop-blur">
            {playsLabel}
          </div>
        </div>

        <p className="line-clamp-1 font-display text-[15px] font-semibold leading-tight text-white">
          {session.title || "Untitled session"}
        </p>
        <p className="mt-0.5 line-clamp-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
          {session.owner_alias || "Anonymous"}
        </p>
        <div className="mt-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-white/45">
          <span>{session.tempo} BPM · {session.musical_key}</span>
          <span>{dur || timeAgo(session.created_at)}</span>
        </div>
      </Link>
    </motion.div>
  );
}

function Tab({ active, onClick, icon, label, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      aria-pressed={active}
      className={`studio-pill flex items-center gap-1.5 rounded-full px-4 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition ${
        active
          ? "bg-fuchsia-500/25 text-fuchsia-100 ring-1 ring-fuchsia-400/40 shadow-[0_0_16px_rgba(232,121,249,0.35)]"
          : "border border-white/[0.08] bg-white/[0.04] text-white/65 hover:bg-white/[0.1] hover:text-white/90"
      }`}
    >
      {icon}{label}
    </button>
  );
}

/** Public shared SONG card — links to /song/{id}. The card's color is derived
 *  from the song's title hash so cards stay visually distinct without needing
 *  real cover art. Music-video shares get a small "VIDEO" overlay pill.
 *  Inline play button toggles a hidden <audio> element — only one song plays
 *  at a time across the gallery (parent owns the "playingId" state). */
function SongCard({ song, idx, playingId, onPlayToggle }) {
  const hash = (song.title || song.id || "x").split("").reduce(
    (h, c) => (h * 31 + c.charCodeAt(0)) % 360, 0
  );
  const hue = hash;
  const dur = fmtDuration(song.duration);
  const playsLabel = Number(song.plays || 0).toLocaleString();
  const isVideo = song.media_type === "video";
  const isPlaying = playingId === song.id;
  const audioRef = useRef(null);

  // Sync the <audio> element when this card becomes (un)active.
  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    if (isPlaying) {
      el.play().catch(() => onPlayToggle(null));
    } else {
      el.pause();
      try { el.currentTime = 0; } catch { /* iOS Safari ignores currentTime on paused media */ }
    }
  }, [isPlaying, onPlayToggle]);

  const togglePlay = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onPlayToggle(isPlaying ? null : song.id);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, delay: Math.min(idx, 12) * 0.03 }}
      data-testid={`explore-song-${song.id}`}
    >
      <Link
        to={`/song/${song.id}`}
        className="group block rounded-2xl border border-white/[0.08] bg-white/[0.025] p-3 transition hover:-translate-y-0.5 hover:border-white/20 hover:bg-white/[0.05] hover:shadow-[0_22px_46px_-24px_rgba(232,121,249,0.45)]"
      >
        <div
          className="relative mb-3 aspect-square overflow-hidden rounded-xl"
          style={{
            background: `radial-gradient(circle at 30% 25%, hsla(${hue}, 90%, 70%, 0.95), transparent 55%),
                         linear-gradient(160deg, hsl(${hue}, 65%, 32%) 0%, hsl(${(hue + 70) % 360}, 70%, 14%) 100%)`,
            boxShadow: "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 12px rgba(0,0,0,0.55)",
          }}
        >
          <Music2 className="absolute inset-0 m-auto h-12 w-12 text-white/85 drop-shadow-[0_4px_16px_rgba(0,0,0,0.6)] transition-transform duration-500 group-hover:scale-110" />
          {isVideo && (
            <span className="absolute left-2 top-2 rounded-full bg-amber-500/40 px-2 py-0.5 font-mono text-[8.5px] uppercase tracking-wider text-amber-50 ring-1 ring-amber-300/40">
              Video
            </span>
          )}
          {/* Inline preview play — only renders for audio shares with a URL. */}
          {!isVideo && song.audio_url && (
            <button
              type="button"
              data-testid={`explore-song-play-${song.id}`}
              onClick={togglePlay}
              title={isPlaying ? "Pause preview" : "Play preview"}
              aria-label={isPlaying ? `Pause preview of ${song.title || "song"}` : `Play preview of ${song.title || "song"}`}
              aria-pressed={isPlaying}
              className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/55 text-white opacity-0 ring-1 ring-white/30 transition hover:bg-fuchsia-500/80 group-hover:opacity-100 focus:opacity-100"
            >
              {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 translate-x-px" />}
            </button>
          )}
          <div className="absolute bottom-2 right-2 rounded-full bg-black/45 px-2 py-0.5 font-mono text-[9px] tabular-nums text-white/85 backdrop-blur">
            {playsLabel}
          </div>
          {!isVideo && song.audio_url && (
            <audio
              ref={audioRef}
              src={song.audio_url}
              preload="none"
              onEnded={() => onPlayToggle(null)}
              onError={() => { if (isPlaying) onPlayToggle(null); }}
              className="hidden"
            />
          )}
        </div>

        <p className="line-clamp-1 font-display text-[15px] font-semibold leading-tight text-white">
          {song.title || "Untitled song"}
        </p>
        <p className="mt-0.5 line-clamp-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
          {song.owner_alias || "Anonymous"}
        </p>
        <div className="mt-2 flex items-center justify-between font-mono text-[10px] uppercase tracking-wider text-white/45">
          <span className="line-clamp-1">{displayModel(song.model) || "AI song"}</span>
          <span>{dur || timeAgo(song.created_at)}</span>
        </div>
      </Link>
    </motion.div>
  );
}

/**
 * `/explore` — the public discovery surface. Lists shared SESSIONS + shared SONGS.
 * Sort modes: New (default) | Trending. Type tabs: Sessions | Songs.
 *
 * No auth, no login wall — the page is meant to convert visitors into creators.
 * Every card links to `/session/{id}` or `/song/{id}` and the top-right CTA
 * pulls them back to the studio at `/`.
 */
export default function ExplorePage() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("sort") === "trending" ? "trending" : "new";
  const kind = params.get("kind") === "songs" ? "songs" : "sessions";
  const [sessions, setSessions] = useState(null);
  const [songs, setSongs] = useState(null);
  const [err, setErr] = useState(null);
  // Single-active-song state — only one SongCard plays at a time.
  const [playingId, setPlayingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (kind === "sessions") {
      setSessions(null); setErr(null);
      axios.get(`${API}/session/share?sort=${tab}&limit=48`)
        .then((res) => { if (!cancelled) setSessions(res.data || []); })
        .catch((e) => {
          if (cancelled) return;
          setErr(safeApiError(e, "Failed to load"));
          setSessions([]);
        });
    } else {
      setSongs(null); setErr(null);
      axios.get(`${API}/song/share?sort=${tab}&limit=48`)
        .then((res) => { if (!cancelled) setSongs(res.data || []); })
        .catch((e) => {
          if (cancelled) return;
          setErr(safeApiError(e, "Failed to load"));
          setSongs([]);
        });
    }
    return () => { cancelled = true; };
  }, [tab, kind]);

  const setTab = (next) => {
    if (next === "new") { params.delete("sort"); }
    else { params.set("sort", next); }
    setParams(params, { replace: true });
  };
  const setKind = (next) => {
    if (next === "sessions") { params.delete("kind"); }
    else { params.set("kind", next); }
    setParams(params, { replace: true });
  };

  // Active list for the current kind
  const items = kind === "sessions" ? sessions : songs;

  return (
    <div className="min-h-screen overflow-x-hidden font-body text-white">
      <title>Explore — ELPM Studio Pro</title>
      <meta name="description" content="Listen to the latest multi-track sessions made on ELPM Studio Pro — voice cloning, AI lyrics, 64-channel mixing in your browser." />
      <AmbientBackdrop />

      <div className="relative z-10 mx-auto max-w-6xl p-4 md:p-8">
        <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="explore-home-link"
            className="group flex items-center gap-3 transition hover:opacity-90"
          >
            <img
              src="/brand/elpm-logo.png"
              alt="ELPM Studio Pro"
              data-testid="brand-wordmark-explore"
              className="h-9 w-auto select-none drop-shadow-[0_2px_14px_rgba(232,121,249,0.22)]"
              draggable="false"
            />
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/45 transition group-hover:text-fuchsia-200">
              ← Back to studio
            </span>
          </Link>
          <Link
            to="/"
            data-testid="explore-open-studio-cta"
            className="studio-pill rounded-2xl border border-white/10 bg-gradient-to-r from-fuchsia-500/20 to-sky-500/20 px-4 py-2 text-sm font-semibold text-white hover:from-fuchsia-500/40 hover:to-sky-500/40"
          >
            <Music2 className="mr-2 inline h-4 w-4" />Make your own →
          </Link>
        </header>

        <motion.section
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card mb-6 rounded-3xl p-6"
        >
          <div className="mb-2 flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.22em] text-fuchsia-300">
            <Sparkles className="h-3.5 w-3.5" />Public · sessions
          </div>
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight md:text-4xl">
            Explore the studio
          </h1>
          <p className="mt-2 max-w-2xl font-body text-[14px] leading-6 text-white/65">
            Press play on any card to hear what people are making with ELPM Studio Pro —
            then open the studio and start your own mix. Every public session here was
            bounced live from a real 64-channel arrangement.
          </p>
        </motion.section>

        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Tab
            active={kind === "sessions"}
            onClick={() => setKind("sessions")}
            icon={<Disc3 className="h-3 w-3" />}
            label="Sessions"
            testId="explore-kind-sessions"
          />
          <Tab
            active={kind === "songs"}
            onClick={() => setKind("songs")}
            icon={<Music2 className="h-3 w-3" />}
            label="Songs"
            testId="explore-kind-songs"
          />
          <span className="mx-2 h-4 w-px bg-white/15" />
          <Tab
            active={tab === "new"}
            onClick={() => setTab("new")}
            icon={<Clock className="h-3 w-3" />}
            label="New"
            testId="explore-tab-new"
          />
          <Tab
            active={tab === "trending"}
            onClick={() => setTab("trending")}
            icon={<Flame className="h-3 w-3" />}
            label="Trending"
            testId="explore-tab-trending"
          />
          <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-white/35">
            {items === null ? "Loading…" : `${items.length} ${kind === "sessions" ? "session" : "song"}${items.length === 1 ? "" : "s"}`}
          </span>
        </div>

        {items === null && (
          <div className="grid place-items-center py-16">
            <Loader2 className="mb-3 h-6 w-6 animate-spin text-fuchsia-300" />
            <p className="font-mono text-[11px] uppercase tracking-wider text-white/45">Loading public {kind}…</p>
          </div>
        )}

        {items !== null && items.length === 0 && (
          <div
            data-testid="explore-empty-state"
            className="rounded-3xl border border-dashed border-white/10 bg-white/[0.02] px-6 py-16 text-center"
          >
            <Share2 className="mx-auto mb-3 h-8 w-8 text-white/30" />
            <p className="font-display text-[18px] font-semibold text-white/85">No public {kind} yet</p>
            <p className="mx-auto mt-2 max-w-md font-body text-[13px] leading-5 text-white/55">
              {err ? `Error loading ${kind}: ${err}` : kind === "sessions"
                ? "Be the first to publish — open the studio, arrange your mix, and hit Share Session."
                : "Be the first to share — generate a song in the studio and hit Share."}
            </p>
            <Link
              to="/"
              data-testid="explore-empty-cta"
              className="mt-5 inline-flex items-center gap-2 rounded-2xl bg-gradient-to-r from-fuchsia-500 to-sky-500 px-5 py-2.5 text-sm font-semibold text-white hover:opacity-90"
            >
              <Music2 className="h-4 w-4" />Open the studio
            </Link>
          </div>
        )}

        {items && items.length > 0 && (
          <div
            data-testid="explore-grid"
            className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6"
          >
            {items.map((item, idx) => (
              kind === "sessions"
                ? <SessionCard key={`expl-s-${item.id}`} session={item} idx={idx} />
                : <SongCard
                    key={`expl-song-${item.id}`}
                    song={item}
                    idx={idx}
                    playingId={playingId}
                    onPlayToggle={setPlayingId}
                  />
            ))}
          </div>
        )}

        <footer className="mt-12 flex items-center justify-between rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-white/45">
          <span className="flex items-center gap-2"><Headphones className="h-3.5 w-3.5" />ELPM · Studio · Pro</span>
          <Link to="/" className="hover:text-white/70">Open studio →</Link>
        </footer>
      </div>
    </div>
  );
}
